'use client'

import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Download, Menu, X, Users } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
  MARKETING_APP_HREF,
  MARKETING_SIGN_IN_HREF,
} from '@/components/landing/marketingAppHref'

const DOWNLOAD_HREF = '/#download-app'

function DownloadCta({
  className = '',
  onNavigate,
  fullWidth = false,
}: {
  className?: string
  onNavigate?: () => void
  fullWidth?: boolean
}) {
  return (
    <Link href={DOWNLOAD_HREF} onClick={onNavigate} className={className}>
      <Button
        className={cn(
          'gap-2 bg-gradient-to-r from-[#F59E0B] via-[#FB7185] to-[#C084FC] px-5 text-white shadow-md shadow-amber-500/20 ring-2 ring-[#FDE68A]/40 hover:from-[#FBBF24] hover:via-[#FB7185] hover:to-[#D8B4FE]',
          fullWidth && 'w-full'
        )}
        size="sm"
      >
        <Download className="h-4 w-4" aria-hidden />
        Download
      </Button>
    </Link>
  )
}

function OpenAppCta({
  className = '',
  onNavigate,
  fullWidth = false,
  surface = 'header',
}: {
  className?: string
  onNavigate?: () => void
  fullWidth?: boolean
  surface?: 'header' | 'menu'
}) {
  return (
    <Link href={MARKETING_APP_HREF} onClick={onNavigate} className={className}>
      <Button
        variant="outline"
        size="sm"
        className={cn(
          fullWidth && 'w-full',
          surface === 'menu'
            ? 'border-[#E8CFAE] bg-white text-[#211725] shadow-sm hover:bg-[#FFF8EF]'
            : 'border-[#FFF6E8]/45 bg-[#FFF6E8]/16 text-[#FFF6E8] shadow-sm backdrop-blur-sm hover:bg-[#FFF6E8]/24 hover:text-white'
        )}
      >
        Open app
      </Button>
    </Link>
  )
}

function SignInCta({
  className = '',
  onNavigate,
  fullWidth = false,
  surface = 'header',
}: {
  className?: string
  onNavigate?: () => void
  fullWidth?: boolean
  surface?: 'header' | 'menu'
}) {
  return (
    <Link
      href={MARKETING_SIGN_IN_HREF}
      onClick={onNavigate}
      className={className}
    >
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          fullWidth && 'w-full',
          surface === 'menu'
            ? 'text-[#211725] hover:bg-[#F6EAD8]'
            : 'text-[#FFF6E8] hover:bg-[#FFF6E8]/10 hover:text-white'
        )}
      >
        Sign in
      </Button>
    </Link>
  )
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const navLinkClass =
    'flex h-full items-center text-sm font-semibold tracking-tight text-[#FFF6E8] transition-colors [text-shadow:0_1px_14px_rgba(0,0,0,0.72)] hover:text-[#FDE68A]'

  const closeMenu = () => setIsMenuOpen(false)

  return (
    <header className="sticky top-0 z-50 w-full border-b border-[#FFF6E8]/10 bg-[radial-gradient(circle_at_20%_35%,rgba(244,114,182,0.18),transparent_30%),radial-gradient(circle_at_58%_42%,rgba(251,191,36,0.13),transparent_34%),linear-gradient(135deg,#0b0712_0%,#17101f_48%,#2a152a_100%)] shadow-[0_12px_40px_-28px_rgba(251,191,36,0.4)] backdrop-blur-xl">
      <div className="container-mobile flex h-20 w-full items-center">
        <div className="flex min-w-0 flex-1 items-center justify-start">
          <a
            href="/"
            className="flex flex-col items-center justify-center gap-1 self-center"
          >
            <div className="flex w-56 items-center justify-center">
              <Image
                src="/wagamour-logo-registered-coral.png"
                width={496}
                height={127}
                alt="Wagamour®"
                className="h-12 w-auto"
                priority
                unoptimized
              />
            </div>
            <span className="w-56 text-center text-xs font-medium leading-tight tracking-tight text-[#FFF6E8] [text-shadow:0_1px_12px_rgba(15,23,42,0.55)]">
              AI for happier, healthier dogs
            </span>
          </a>
        </div>

        <nav className="hidden h-full shrink-0 items-center space-x-8 md:flex">
          <a href="/#features" className={navLinkClass}>
            Product
          </a>
          <a href="/use-cases" className={navLinkClass}>
            Use cases
          </a>
          <a href="/how-it-works" className={navLinkClass}>
            How it Works
          </a>
          <a href="/about" className={navLinkClass}>
            About
          </a>
          <a href="/#providers" className={`${navLinkClass} gap-1.5`}>
            <Users className="h-4 w-4 flex-shrink-0" />
            <span>For Providers</span>
          </a>
        </nav>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
          <DownloadCta className="hidden md:block" />
          <OpenAppCta className="hidden lg:block" />
          <SignInCta className="hidden md:block" />

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-[#FFF6E8] hover:bg-[#FFF6E8]/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="border-t border-[#FFF6E8]/18 bg-[#FFF6E8]/90 backdrop-blur-xl md:hidden">
          <nav className="container-mobile space-y-4 py-4">
            <a
              href="/#features"
              className="block text-sm font-semibold text-[#211725] transition-colors hover:text-[#9A5B1A]"
              onClick={closeMenu}
            >
              Product
            </a>
            <a
              href="/use-cases"
              className="block text-sm font-semibold text-[#211725] transition-colors hover:text-[#9A5B1A]"
              onClick={closeMenu}
            >
              Use cases
            </a>
            <a
              href="/how-it-works"
              className="block text-sm font-semibold text-[#211725] transition-colors hover:text-[#9A5B1A]"
              onClick={closeMenu}
            >
              How it Works
            </a>
            <a
              href="/about"
              className="block text-sm font-semibold text-[#211725] transition-colors hover:text-[#9A5B1A]"
              onClick={closeMenu}
            >
              About
            </a>
            <a
              href="/#providers"
              className="flex items-center gap-1 text-sm font-semibold text-[#211725] transition-colors hover:text-[#9A5B1A]"
              onClick={closeMenu}
            >
              <Users className="h-4 w-4" />
              For Providers
            </a>
            <div className="space-y-2 border-t pt-4">
              <DownloadCta className="block" onNavigate={closeMenu} fullWidth />
              <OpenAppCta
                className="block"
                onNavigate={closeMenu}
                fullWidth
                surface="menu"
              />
              <SignInCta
                className="block"
                onNavigate={closeMenu}
                fullWidth
                surface="menu"
              />
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
